// CreateLaunchClassicPrefill.js
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Button,
  Card,
  Form,
  Badge,
  ProgressBar,
  Alert,
  Spinner
} from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faListCheck,
  faCopy,
  faPlus,
  faBolt
} from '@fortawesome/free-solid-svg-icons';

import useAdvanceTable from '../../hooks/useAdvanceTable.js';
import AdvanceTableProvider, {
  useAdvanceTableContext
} from '../../providers/AdvanceTableProvider.js';
import FilterTab from '../../components/common/FilterTab.js';
import SearchBox from '../../components/common/SearchBox.js';
import AdvanceTable from '../../components/base/AdvanceTable.js';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter.js';

import CreateWizzardForm from '../../components/project-management/create-new/CreateWizzardForm.js';
import ProductColorDot from '../../components/common/ProductColorDot.js';
import { CABLE_COLOR_HEX } from '../../helpers/productColor.js';
import { API } from '../../config/api';
import {
  findStandardFor,
  describeLaunchLine
} from '../../helpers/standardMatch';

const resColorHexOf = c => CABLE_COLOR_HEX[String(c || '').toUpperCase()] || '#8892a0';

// =====================
// utils
// =====================
const U = v =>
  String(v ?? '')
    .trim()
    .toUpperCase();
const fmt = n => Number(n || 0).toLocaleString();

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const specOf = p => {
  const n = toNum(p?.numberOfConductors);
  const s = toNum(p?.section);
  const hasMixed = Boolean(p?.hasMixedConductorSections);
  const gy = hasMixed ? Number(p?.greenYellowSection) || null : null;
  return hasMixed ? `${n}×${s} + GY ${gy ?? '—'}` : `${n}×${s}`;
};

const minDateStr = arr => {
  const dates = (arr || [])
    .map(x => x?.dueDate)
    .filter(Boolean)
    .map(d => new Date(d).getTime())
    .filter(Number.isFinite);

  if (!dates.length) return null;
  const min = Math.min(...dates);
  return new Date(min).toISOString().slice(0, 10);
};

const deliveryBadge = kind => {
  if (kind === 'ORDERS') return { label: 'ORDERS', color: 'primary' };
  return { label: 'STOCK', color: 'secondary' };
};

// =====================
// group items[] -> rows by productKey (TABLE step)
// =====================
function groupLaunchItems(items = [], launchKind = 'ORDERS') {
  const byKey = new Map();

  for (const it of items) {
    const key = it?.productKey || '';
    if (!key) continue;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(it);
  }

  const rows = [];
  for (const [productKey, group] of byKey.entries()) {
    const p = group?.[0]?.product || {};
    const cableName = U(p.cableName);
    const color = U(p.color);
    const spec = specOf(p);

    const totalOrderedLength = group.reduce(
      (s, x) => s + toNum(x.totalOrderedLength),
      0
    );
    const remainingToPlan = group.reduce(
      (s, x) => s + toNum(x.remainingToPlan),
      0
    );
    const launchLength = group.reduce((s, x) => s + toNum(x.launchLength), 0);

    const clampedLaunch = clamp(
      launchLength,
      0,
      remainingToPlan || totalOrderedLength || launchLength
    );

    rows.push({
      productKey,
      cableName,
      color,
      spec,
      sourceKind: launchKind,
      linesCount: group.length,
      dueDate: minDateStr(group),
      totalOrderedLength,
      remainingToPlan: Math.max(0, remainingToPlan - clampedLaunch),
      launchLength: clampedLaunch,
      rawItems: group,
      product: p
    });
  }

  rows.sort((a, b) => {
    const da = a.dueDate
      ? new Date(a.dueDate).getTime()
      : Number.POSITIVE_INFINITY;
    const db = b.dueDate
      ? new Date(b.dueDate).getTime()
      : Number.POSITIVE_INFINITY;
    if (da !== db) return da - db;
    return String(a.cableName).localeCompare(String(b.cableName));
  });

  return rows;
}

// =====================
// TOP SECTION (Filter + Search + Classic button)
// =====================
const LaunchTopSection = ({ selectedCount = 0, onClassic }) => {
  const { setGlobalFilter, getPrePaginationRowModel, getColumn } =
    useAdvanceTableContext();

  const handleFilterItemClick = (columnId, value) => {
    const column = getColumn(columnId);
    column?.setFilterValue(value === 'all' ? '' : value);
  };

  const tabItems = useMemo(() => {
    const rows = getPrePaginationRowModel().rows;

    const counts = new Map();
    for (const r of rows) {
      const name = U(r?.original?.cableName);
      if (!name) continue;
      counts.set(name, (counts.get(name) || 0) + 1);
    }

    const names = Array.from(counts.keys()).sort((a, b) => a.localeCompare(b));
    const MAX_TABS = 10;
    const visible = names.slice(0, MAX_TABS);

    const base = [
      {
        label: 'All',
        value: 'all',
        onClick: () => handleFilterItemClick('cableName', 'all'),
        count: rows.length
      }
    ];

    const dynamic = visible.map(name => ({
      label: name,
      value: name,
      onClick: () => handleFilterItemClick('cableName', name),
      count: counts.get(name) || 0
    }));

    return [...base, ...dynamic];
  }, [getPrePaginationRowModel, getColumn]);

  const handleSearchInputChange = e =>
    setGlobalFilter(e.target.value || undefined);

  return (
    <Row className="g-3 justify-content-between align-items-center mb-3">
      <Col xs={12} sm="auto">
        <FilterTab tabItems={tabItems} />
      </Col>

      <Col xs={12} sm="auto">
        <div className="d-flex align-items-center gap-2">
          <SearchBox
            onChange={handleSearchInputChange}
            placeholder="Search (product, spec...)"
            style={{ maxWidth: '30rem' }}
          />
          <Button
            variant="phoenix-secondary"
            disabled={selectedCount <= 0}
            onClick={onClassic}
            title="Classic = prefill + apoi wizard complet"
          >
            <FontAwesomeIcon icon={faListCheck} className="me-2" />
            Classic launch ({selectedCount})
          </Button>
        </div>
      </Col>
    </Row>
  );
};

// =====================
// TABLE columns
// =====================
export const launchProductsColumns = [
  {
    id: 'cableName',
    header: 'Cable',
    accessorFn: row => row.cableName,
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semi-bold">{original.cableName || '—'}</span>
          <Badge bg="secondary" className="fs-10 rounded-pill">
            {original.color || '—'}
          </Badge>
          <Badge
            bg={deliveryBadge(original.sourceKind).color}
            className="fs-10 rounded-pill"
          >
            {deliveryBadge(original.sourceKind).label}
          </Badge>
        </div>
        <span className="text-muted fs-10">Key: {original.productKey}</span>
      </div>
    ),
    meta: {
      headerProps: { style: { minWidth: 260 } },
      cellProps: { className: 'py-3' }
    }
  },
  {
    accessorKey: 'spec',
    header: 'Spec',
    cell: ({ row: { original } }) => (
      <span className="fw-semi-bold white-space-nowrap">
        {original.spec || '—'}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '14%' }, className: 'text-center' },
      cellProps: { className: 'text-center py-3' }
    }
  },
  {
    accessorKey: 'linesCount',
    header: 'Lines',
    cell: ({ row: { original } }) => (
      <span className="fw-semi-bold">{fmt(original.linesCount)}</span>
    ),
    meta: {
      headerProps: { style: { width: '8%' }, className: 'text-center' },
      cellProps: { className: 'text-center py-3' }
    }
  },
  {
    accessorKey: 'dueDate',
    header: 'Due (min)',
    cell: ({ row: { original } }) => (
      <span className="white-space-nowrap">
        {original.dueDate || <span className="text-muted">—</span>}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-center' },
      cellProps: { className: 'text-center py-3' }
    }
  },
  {
    accessorKey: 'remainingToPlan',
    header: 'Remaining (m)',
    cell: ({ row: { original } }) => (
      <span className="fw-bold">{fmt(original.remainingToPlan)}</span>
    ),
    meta: {
      headerProps: { style: { width: '12%' }, className: 'text-end' },
      cellProps: { className: 'text-end py-3' }
    }
  },
  {
    accessorKey: 'launchLength',
    header: 'Planned launch (m)',
    cell: ({ row: { original } }) => (
      <span className="fw-bold">{fmt(original.launchLength)}</span>
    ),
    meta: {
      headerProps: { style: { width: '12%' }, className: 'text-end' },
      cellProps: { className: 'text-end py-3' }
    }
  }
];

const LaunchProductsTable = () => (
  <div className="border-bottom">
    <AdvanceTable
      tableProps={{
        className: 'phoenix-table border-top border-200 fs-9 table-fixed'
      }}
    />
    <AdvanceTableFooter pagination className="py-3" />
  </div>
);

// =====================
// PREFORM MODEL
// =====================
const createEmptyProduct = () => ({
  name: '',
  numberOfConductors: '',
  section: '',
  lengthTotal: 0,
  lengthToOrders: 0,
  lengthToStock: 0,
  color: '',
  hasMixedConductorSections: false,
  greenYellowSection: '',
  sourceKind: 'MIXED',
  productKey: '',
  dueDate: null,
  ordersRemainingTotal: 0,
  rawItems: [],
  allocations: []
});

// IMPORTANT:
// - lengthToOrders este CLAMP la pool
// - dacă lengthTotal > pool => diferența devine stock (lengthToStock)
function normalizeRow(row) {
  const total = Math.max(0, toNum(row.lengthTotal));
  const pool = Math.max(0, toNum(row.ordersRemainingTotal));
  // lengthToOrders can never exceed what we actually produce (lengthTotal) NOR
  // the remaining order pool. Capping by `total` is what prevents a 3000 m run
  // from allocating the full 4000 m order line (FULLY instead of PARTIALLY).
  const toOrders = clamp(toNum(row.lengthToOrders), 0, Math.min(pool, total));
  const toStock = Math.max(0, total - toOrders);

  return {
    ...row,
    lengthTotal: total,
    lengthToOrders: toOrders,
    lengthToStock: toStock
  };
}

// pool orders pt un grup: dacă remainingToPlan e 0 (simulation), folosim launchLength
function calcOrdersPoolFromRawItems(rawItems = []) {
  return (rawItems || []).reduce((s, it) => {
    const rem = toNum(it?.remainingToPlan);
    const len = toNum(it?.launchLength);
    return s + (rem > 0 ? rem : len);
  }, 0);
}

// =====================
// Allocation builder (FIFO) — FIXED + SIMPLE
// =====================
function sortOrderLinesFifo(lines = []) {
  return [...lines].sort((a, b) => {
    const da = a?.dueDate
      ? new Date(a.dueDate).getTime()
      : Number.POSITIVE_INFINITY;
    const db = b?.dueDate
      ? new Date(b.dueDate).getTime()
      : Number.POSITIVE_INFINITY;
    if (da !== db) return da - db;

    const oa = String(a?.orderId || '');
    const ob = String(b?.orderId || '');
    if (oa !== ob) return oa.localeCompare(ob);

    return String(a?.lineId || '').localeCompare(String(b?.lineId || ''));
  });
}

// IMPORTANT: în simulation remainingToPlan e 0, deci fallback pe launchLength
function getLineRemainingMeters(line) {
  const rem = toNum(line?.remainingToPlan);
  const len = toNum(line?.launchLength);
  return Math.max(0, rem > 0 ? rem : len);
}

const lineKey = l => `${l?.orderId ?? ''}::${l?.lineId ?? ''}`;

/**
 * ✅ FIX: pool per productKey = UNION (dedupe) din rawItems de pe toate cardurile
 * Ca să meargă și când ai splitCardByOrderLines (rawItems: [line])
 */
function buildAllocationsForAllJobs(products = []) {
  // 1) union + dedupe pool per key
  const pool = new Map(); // key -> { lines: [{orderId,lineId,dueDate,remaining}], cursor }

  for (const p of products || []) {
    const key = p?.productKey;
    if (!key) continue;

    if (!pool.has(key)) {
      pool.set(key, { byLine: new Map(), lines: [], cursor: 0 });
    }

    const state = pool.get(key);

    for (const raw of p?.rawItems || []) {
      const lk = lineKey(raw);
      if (!lk || lk === '::') continue;

      const incoming = {
        orderId: raw.orderId,
        lineId: raw.lineId,
        dueDate: raw.dueDate,
        remaining: getLineRemainingMeters(raw)
      };

      const existing = state.byLine.get(lk);
      if (!existing) {
        state.byLine.set(lk, incoming);
      } else {
        // merge (safe): keep earliest dueDate + max remaining
        const ed = existing?.dueDate
          ? new Date(existing.dueDate).getTime()
          : Number.POSITIVE_INFINITY;
        const nd = incoming?.dueDate
          ? new Date(incoming.dueDate).getTime()
          : Number.POSITIVE_INFINITY;

        state.byLine.set(lk, {
          ...existing,
          remaining: Math.max(
            toNum(existing.remaining),
            toNum(incoming.remaining)
          ),
          dueDate: nd < ed ? incoming.dueDate : existing.dueDate
        });
      }
    }
  }

  // finalize sort
  for (const [, state] of pool.entries()) {
    state.lines = sortOrderLinesFifo(Array.from(state.byLine.values()));
    state.cursor = 0;
    delete state.byLine;
  }

  // 2) allocate by job index (card order)
  const allocations = [];

  for (let jobIndex = 0; jobIndex < (products || []).length; jobIndex++) {
    const job = products[jobIndex];
    const key = job?.productKey;
    // never allocate more to orders than we actually produce (lengthTotal)
    const lt = toNum(job?.lengthTotal);
    const take = Math.max(
      0,
      lt > 0
        ? Math.min(toNum(job?.lengthToOrders), lt)
        : toNum(job?.lengthToOrders)
    );
    if (!key || take <= 0) continue;

    const state = pool.get(key);
    if (!state) continue;

    let need = take;

    while (need > 0 && state.cursor < state.lines.length) {
      const line = state.lines[state.cursor];
      const canTake = Math.max(0, Math.min(toNum(line.remaining), need));

      if (canTake > 0) {
        allocations.push({
          jobIndex,
          productKey: key,
          orderId: line.orderId,
          lineId: line.lineId,
          allocatedMeters: canTake
        });

        line.remaining -= canTake;
        need -= canTake;
      }

      if (line.remaining <= 0) state.cursor += 1;
      if (canTake <= 0 && line.remaining > 0) break;
    }
  }

  return allocations;
}

/** mapare simplă index -> allocations */
function attachAllocationsToProducts(products = [], allocations = []) {
  const byIndex = new Map();
  for (const a of allocations || []) {
    const idx = Number(a?.jobIndex);
    if (!Number.isFinite(idx)) continue;
    if (!byIndex.has(idx)) byIndex.set(idx, []);
    byIndex.get(idx).push({
      orderId: a.orderId,
      lineId: a.lineId,
      allocatedMeters: toNum(a.allocatedMeters)
    });
  }

  return (products || []).map((p, idx) => ({
    ...p,
    allocations: byIndex.get(idx) || []
  }));
}

/** Normalize an order line's deliveryPlan (NEW items[] OR legacy drums[]/coils[])
 * into the delivery-item chip shape the wizard's Order Context renders.
 * `qtyPlanned` here is the REMAINING to do = full qty − already launched (the
 * order tracks launched units per item in `tokenIds`), so Order Context + Order
 * Reservation show only what's left to launch. Fully-done items are dropped. */
const normalizePlanItems = plan => {
  if (!plan) return [];
  const items = Array.isArray(plan.items) ? plan.items : [];
  const out = [];
  if (items.length) {
    items.forEach((it, i) => {
      const qty = toNum(it.qtyPlanned ?? it.quantity);
      const len = toNum(
        it.lengthPerUnit ?? it.length ?? it.lengthPerDrum ?? it.lengthPerCoil
      );
      if (!qty || !len) return;
      // Units already launched for this item across prior jobs: prefer launchedQty
      // (counts coil UNITS correctly), fall back to tokenIds.length (1 per drum).
      const launched =
        Number(it.launchedQty) > 0
          ? Number(it.launchedQty)
          : Array.isArray(it.tokenIds)
            ? it.tokenIds.length
            : 0;
      const remaining = Math.max(0, qty - launched);
      if (remaining <= 0) return; // already fully launched in prior jobs
      const kind = String(it.kind || '').toUpperCase() === 'DRUM' ? 'DRUM' : 'COIL';
      out.push({
        itemId: it.itemId || `it-${i}`,
        kind,
        lengthPerUnit: len,
        qtyPlanned: remaining,
        drumMaterial: kind === 'DRUM' ? it.drumMaterial || null : null,
        drumDiameter: kind === 'DRUM' ? it.drumDiameter || null : null
      });
    });
  } else {
    (plan.drums || []).forEach((d, i) => {
      const qty = toNum(d.quantity);
      const len = toNum(d.lengthPerDrum);
      if (!qty || !len) return;
      out.push({
        itemId: d.itemId || `drum-${i}`,
        kind: 'DRUM',
        lengthPerUnit: len,
        qtyPlanned: qty,
        drumMaterial: d.drumMaterial || null,
        drumDiameter: d.drumDiameter || null
      });
    });
    (plan.coils || []).forEach((c, i) => {
      const qty = toNum(c.quantity);
      const len = toNum(c.lengthPerCoil);
      if (!qty || !len) return;
      out.push({
        itemId: c.itemId || `coil-${i}`,
        kind: 'COIL',
        lengthPerUnit: len,
        qtyPlanned: qty
      });
    });
  }
  return out;
};

/** Build the wizard's "Order Context" (_orders) from a product's raw order-line
 * items, so create-launch shows the same Order Context card + delivery-item chips
 * as plan-next-phase when the launch is for orders. Aggregated per order. */
const buildOrdersContext = (rawItems = []) => {
  const byOrder = new Map();
  for (const it of rawItems || []) {
    const key = it?.orderId || it?.orderCode || it?.lineId;
    if (!key) continue;
    const alloc = toNum(it.launchLength); // meters launched for this order in this job
    const total = toNum(it.totalOrderedLength);
    const planItems = normalizePlanItems(it.deliveryPlan);
    if (!byOrder.has(key)) {
      byOrder.set(key, {
        orderId: it.orderId || null,
        orderCode: it.orderCode || null,
        client: it.client || null,
        dueDate: it.dueDate || null,
        plannedLength_m: total,
        allocatedFromThisJob_m: alloc,
        deliveryPlanItems: [...planItems]
      });
    } else {
      const e = byOrder.get(key);
      e.allocatedFromThisJob_m += alloc;
      e.plannedLength_m += total;
      e.deliveryPlanItems.push(...planItems);
    }
  }
  return [...byOrder.values()];
};

/** meta launch in standardData (fără rawItems) */
const buildLaunchMeta = p => ({
  sourceKind: p?.sourceKind || 'MIXED',
  productKey: p?.productKey || null,
  dueDate: p?.dueDate || null,
  lengthTotal: toNum(p?.lengthTotal),
  lengthToOrders: Math.max(0, toNum(p?.lengthToOrders)),
  lengthToStock: Math.max(0, toNum(p?.lengthToStock)),
  ordersRemainingTotal: Math.max(0, toNum(p?.ordersRemainingTotal))
});

// =====================
// MAIN
// =====================
export default function CreateLaunchClassicPrefill() {
  const location = useLocation();
  const navigate = useNavigate();
  // Future-products sandbox mode: the same wizard, but submit prepares an ISOLATED
  // future product (futureJob:) instead of launching a real job at the machines.
  const isFuture = !!location.state?.future;
  const launchRequest = location.state?.launchRequest;
  // Preview/Edit of an existing future product: skip the simulate step, open the
  // wizard straight on the product's saved standardData, and PUT-update on save.
  const prefillStandardData = location.state?.prefillStandardData || null;
  const futureEditId = location.state?.futureEditId || null;

  // Straight to the pre-fill step with one blank card, for a product that comes
  // from nobody's order.
  //
  // Every route into this page used to start from an ORDER LINE, which left no
  // way at all to plan something for stock — and on a day with no open lines,
  // Future Products had nothing to show and nothing to do. The pre-fill step
  // already had `addCard()` for typing a product in by hand; it was simply
  // unreachable unless an order line had been selected first.
  const startBlank = !!location.state?.startBlank;
  const missing = !launchRequest && !prefillStandardData && !startBlank;

  const launchKind = launchRequest?.launchKind ?? (startBlank ? 'STOCK' : 'ORDERS');
  const items = launchRequest?.items ?? [];

  const [step, setStep] = useState(startBlank ? 'PREFORM' : 'TABLE'); // TABLE | PREFORM
  const [products, setProducts] = useState(startBlank ? [createEmptyProduct()] : []);

  // Wizard state — prefilled directly when editing a future product
  const [standardsData, setStandardsData] = useState(prefillStandardData);
  const [wizardError, setWizardError] = useState('');
  const [wizardLoading, setWizardLoading] = useState(false);

  const clearStandardsData = () => setStandardsData(null);

  // ---- TABLE step
  const tableData = useMemo(
    () => groupLaunchItems(items, launchKind),
    [items, launchKind]
  );

  const table = useAdvanceTable({
    data: tableData,
    columns: launchProductsColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: true
  });

  const selectedRows = table.getSelectedRowModel().rows;
  const selectedGroups = selectedRows.map(r => r.original);

  const remainingPoolForKey = useCallback(
    (key, currentIndex = -1, arr = products) => {
      if (!key) return 0;

      const any = arr.find(p => p.productKey === key);
      const total = Math.max(0, toNum(any?.ordersRemainingTotal ?? 0));

      let used = 0;
      for (let i = 0; i < arr.length; i++) {
        const p = arr[i];
        if (p.productKey !== key) continue;
        if (i === currentIndex) continue;
        used += toNum(p.lengthToOrders);
      }

      return Math.max(0, total - used);
    },
    [products]
  );

  const setRow = useCallback((idx, updater) => {
    setProducts(prev => {
      const copy = [...prev];
      const cur = copy[idx];
      if (!cur) return prev;

      const next = typeof updater === 'function' ? updater(cur) : updater;
      copy[idx] = normalizeRow(next);

      return copy;
    });
  }, []);

  // Pre-fill Order Reservation: how many of each final drum/coil the launched
  // meters are reserved for (per order delivery item). Stored on the product as
  // _reservation (itemId -> qty) and carried into the wizard.
  const setReservationQty = useCallback((idx, itemId, qty, maxQty = Infinity) => {
    setProducts(prev => {
      const copy = [...prev];
      const cur = copy[idx];
      if (!cur) return prev;
      const res = { ...(cur._reservation || {}) };
      const clamped = Math.max(0, Math.min(qty, maxQty));
      if (clamped > 0) res[itemId] = clamped;
      else delete res[itemId];
      copy[idx] = { ...cur, _reservation: res };
      return copy;
    });
  }, []);

  const addCard = useCallback(() => {
    setProducts(prev => [...prev, createEmptyProduct()]);
  }, []);

  const removeCard = useCallback(idx => {
    setProducts(prev => prev.filter((_, i) => i !== idx));
  }, []);

  // manual split (clone empty meters)
  const splitCard = useCallback(idx => {
    setProducts(prev => {
      const copy = [...prev];
      const base = copy[idx];
      if (!base) return prev;

      const clone = normalizeRow({
        ...base,
        lengthTotal: 0,
        lengthToOrders: 0,
        lengthToStock: 0
      });

      copy.splice(idx + 1, 0, clone);
      return copy;
    });
  }, []);

  // ✅ PRO split: 1 card per order line
  const splitCardByOrderLines = useCallback(idx => {
    setProducts(prev => {
      const base = prev[idx];
      if (!base) return prev;

      const lines = sortOrderLinesFifo(base.rawItems || []);
      if (!lines.length) return prev;

      const cards = lines.map(l => {
        const meters = Math.max(0, toNum(l.launchLength)); // simulation
        return normalizeRow({
          ...base,
          dueDate: l.dueDate || base.dueDate,
          rawItems: [l], // ok — allocations funcționează (pool e UNION)
          lengthTotal: meters,
          lengthToOrders: meters,
          lengthToStock: 0
        });
      });

      const next = [...prev];
      next.splice(idx, 1, ...cards);
      return next;
    });
  }, []);

  // build initial cards from selection
  const startClassicPrefill = useCallback(() => {
    if (!selectedGroups.length) return;

    const rows = selectedGroups.map(g => {
      const p = g.product || {};
      const raw = g.rawItems || [];

      const ordersPool = calcOrdersPoolFromRawItems(raw);
      const plannedTotal = Math.max(0, toNum(g.launchLength));

      const base = {
        ...createEmptyProduct(),
        name: p.cableName || g.cableName || '',
        numberOfConductors: toNum(p.numberOfConductors),
        section: toNum(p.section),
        color: p.color || g.color || '',
        hasMixedConductorSections: Boolean(p.hasMixedConductorSections),
        greenYellowSection: p.greenYellowSection ?? '',
        sourceKind: 'MIXED',
        productKey: g.productKey,
        dueDate: g.dueDate,

        ordersRemainingTotal: ordersPool,
        rawItems: raw
      };

      return normalizeRow({
        ...base,
        lengthTotal: plannedTotal,
        lengthToOrders: plannedTotal
      });
    });

    setProducts(rows);
    setStep('PREFORM');
  }, [selectedGroups]);

  // Standard lookup (colour included) lives in helpers/standardMatch.js — see
  // the note there about why launching three colours at once used to produce
  // three jobs of the same colour.

  // =====================
  // Continue -> Wizard
  // =====================
  const continueToWizard = useCallback(async () => {
    const ok = (products || []).every(
      p =>
        String(p.name || '').trim() &&
        toNum(p.numberOfConductors) > 0 &&
        toNum(p.section) > 0 &&
        toNum(p.lengthTotal) > 0 &&
        String(p.color || '').trim()
    );

    if (!ok) {
      setWizardError(
        'Completează toate câmpurile obligatorii (name, conductors, section, total length, color).'
      );
      return;
    }

    setWizardError('');
    setWizardLoading(true);

    try {
      // 0) normalize
      const normalized = (products || []).map(p => normalizeRow(p));

      // 1) allocations FIFO
      const allocations = buildAllocationsForAllJobs(normalized);

      // 2) attach allocations (index mapping)
      const withAlloc = attachAllocationsToProducts(normalized, allocations);

      // 3) standards payload
      const standardsPayload = withAlloc.map(p => ({
        name: String(p.name || '').trim(),
        numberOfConductors: toNum(p.numberOfConductors),
        section: toNum(p.section),
        length: toNum(p.lengthTotal),
        color: String(p.color || '').trim(),
        hasMixedConductorSections: Boolean(p.hasMixedConductorSections),
        greenYellowSection: Boolean(p.hasMixedConductorSections)
          ? String(p.greenYellowSection || '').trim()
          : ''
      }));

      const response = await axios.post(
        `${API.BACKEND}/api/v1/standard/find-many`,
        standardsPayload
      );

      const standards = response.data.data || [];

      // 4) final standardData = standard + _launch + _allocations (1:1 index)
      const standardizedData = standardsPayload.map((local, idx) => {
        const standard = findStandardFor(standards, local);
        if (!standard) {
          throw new Error(
            `No standard found for item #${idx + 1}: ${describeLaunchLine(local)}`
          );
        }

        const cloned = structuredClone(standard);
        const lp = withAlloc[idx] || {};

        // Order Reservation is a PLAN only — carried as data (_reservation), NOT
        // auto-created as executions. The planner adds executions in the wizard.
        const ordersCtx = buildOrdersContext(lp.rawItems);

        return {
          ...cloned,
          // carry the real standard id (like CreateNewProduct) so the launched job
          // gets job.standardId → HMI phase-details work. `_id` is mangled below, so
          // capture the clean id here (cloned._id is still the original standard _id).
          standardId: cloned._id,
          _id: `${cloned._id}_${idx}`,
          length: toNum(local.length),

          _launch: buildLaunchMeta(lp),
          _allocations: Array.isArray(lp.allocations) ? lp.allocations : [],
          _orders: ordersCtx,
          _reservation: lp._reservation || {},

          phases: (cloned.phases || []).map((phase, i, arr) => {
            const previousPhase = i > 0 ? arr[i - 1].name : null;

            if (phase.name === 'conductor_isolation') {
              // Single-conductor standards (FY, MYF, …) store wire/isolation flat
              // on the phase instead of a conductors[] array, so the wizard's
              // conductor card renders empty. Normalize to conductors[] here —
              // same shape the backend job-build produces (jobHelpers ~1876).
              const { wire, isolation, ...rest } = phase;
              const rawConductors =
                Array.isArray(phase.conductors) && phase.conductors.length
                  ? phase.conductors
                  : wire && isolation
                    ? [{ wire, isolation }]
                    : [];
              return {
                ...rest,
                dependsOn: previousPhase,
                conductors: rawConductors.map(c => ({
                  ...c,
                  execution: []
                }))
              };
            }

            return { ...phase, dependsOn: previousPhase, execution: [] };
          })
        };
      });

      setStandardsData(standardizedData);
    } catch (err) {
      console.error(err);
      // The useful case is "no standard for FY 1x2.5 ORANGE" — a colour variant
      // that does not exist in the collection. Swallowing it behind a generic
      // line left the planner with nothing to act on.
      setWizardError(err?.message || 'Unable to fetch standards for wizard.');
    } finally {
      setWizardLoading(false);
    }
  }, [products]);

  // =====================
  // WIZARD
  // =====================
  if (standardsData) {
    return (
      <CreateWizzardForm
        standardData={standardsData}
        setStandardsData={setStandardsData}
        clearStandardsData={clearStandardsData}
        handleSubmitFc={async ({ standardData, mergedPhases, groupByPhases }) => {
          // FUTURE mode → prepare an isolated future product (no machine launch).
          if (isFuture) {
            const body = {
              products: standardData,
              mergedPhases,
              groupByPhases,
              destination: 'MIXED'
            };
            // editing an existing future product → PUT (same futureId), else create
            const res = futureEditId
              ? await axios.put(
                  `${API.REDIS}/api/redis/future-products/${futureEditId}`,
                  body
                )
              : await axios.post(
                  `${API.REDIS}/api/redis/future-products`,
                  body
                );
            clearStandardsData();
            navigate('/apps/project-management/future-products');
            return res.data;
          }

          const res = await axios.post(`${API.REDIS}/api/redis/orders-new-jobs`, {
            standardData,
            mergedPhases,
            groupByPhases
          });

          console.log('Jobs created:', res.data);
          clearStandardsData();
          return res.data;
        }}
      />
    );
  }

  if (missing) {
    return (
      <Navigate to="/apps/project-management/order-simulation" replace />
    );
  }

  // =====================
  // TABLE
  // =====================
  if (step === 'TABLE') {
    return (
      <div className="mb-9">
        <AdvanceTableProvider {...table}>
          <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center">
            <h2 className="mb-0">
              <span className="me-3">Launch Groups</span>{' '}
              <span className="fw-normal text-body-tertiary">
                ({tableData.length})
              </span>
            </h2>
            <span className="text-muted">
              Source: <b>{launchKind}</b> • Lines: <b>{fmt(items.length)}</b>
            </span>
          </div>

          <LaunchTopSection
            selectedCount={selectedGroups.length}
            onClassic={startClassicPrefill}
          />
          <LaunchProductsTable />
        </AdvanceTableProvider>
      </div>
    );
  }

  // =====================
  // PREFORM
  // =====================
  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h1 className="fw-semibold mb-0">Classic Launch — Pre-fill</h1>

        <div className="d-flex gap-2">
          {/* With no order lines behind this there is no table to go back to —
              an empty one would be a dead end. */}
          <Button
            variant="outline-secondary"
            onClick={() => (startBlank ? navigate(-1) : setStep('TABLE'))}
          >
            Back
          </Button>
          {startBlank && (
            <Button variant="outline-primary" onClick={addCard}>
              <FontAwesomeIcon icon={faCopy} className="me-2" />
              Add another product
            </Button>
          )}

          <Button
            variant="primary"
            disabled={wizardLoading}
            onClick={continueToWizard}
          >
            {wizardLoading ? (
              <Spinner size="sm" />
            ) : (
              <FontAwesomeIcon icon={faBolt} className="me-2" />
            )}
            Continue to Wizard
          </Button>
        </div>
      </div>

      {wizardError && <Alert variant="danger">{wizardError}</Alert>}

      <Form>
        {products.map((p, index) => {
          const key = p.productKey || '';
          const poolTotal = Math.max(0, toNum(p.ordersRemainingTotal));
          const poolLeftExclThis = remainingPoolForKey(key, index);
          const maxOrdersForThisCard = Math.max(
            0,
            poolLeftExclThis + toNum(p.lengthToOrders)
          );

          const after = Math.max(
            0,
            maxOrdersForThisCard - toNum(p.lengthToOrders)
          );

          const coveragePct =
            poolTotal > 0
              ? Math.min(
                  100,
                  Math.round((toNum(p.lengthToOrders) / poolTotal) * 100)
                )
              : 0;

          return (
            <Card
              key={`${p.productKey || 'NA'}-${index}`}
              className="mb-4 shadow-sm"
            >
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div className="fw-semibold">
                  Product #{index + 1}{' '}
                  {/* Read off the row rather than asserted: a card with nothing
                      allocated to an order is not an ORDERS+STOCK card, and a
                      product typed in by hand never is. */}
                  <Badge
                    bg={toNum(p.lengthToOrders) > 0 ? 'primary' : 'secondary'}
                    className="ms-2"
                  >
                    {toNum(p.lengthToOrders) > 0
                      ? toNum(p.lengthToStock) > 0
                        ? 'ORDERS+STOCK'
                        : 'ORDERS'
                      : 'STOCK'}
                  </Badge>
                  <span className="text-muted ms-2 fs-10">
                    due: {p.dueDate || '—'} • key: {p.productKey || '—'}
                  </span>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => splitCard(index)}
                  >
                    <FontAwesomeIcon icon={faCopy} className="me-2" />
                    Split
                  </Button>

                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => splitCardByOrderLines(index)}
                  >
                    Split by lines
                  </Button>

                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => removeCard(index)}
                  >
                    Delete
                  </Button>
                </div>
              </Card.Header>

              <Card.Body>
                <Row className="g-3 align-items-start">
                  <Col lg={7}>
                <Row className="mb-3">
                  <Col md={3}>
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      value={p.name}
                      onChange={e =>
                        setRow(index, cur => ({ ...cur, name: e.target.value }))
                      }
                    />
                  </Col>

                  <Col md={3}>
                    <Form.Label>Conductors *</Form.Label>
                    <Form.Control
                      type="number"
                      value={p.numberOfConductors}
                      onChange={e =>
                        setRow(index, cur => ({
                          ...cur,
                          numberOfConductors: toNum(e.target.value)
                        }))
                      }
                    />
                  </Col>

                  <Col md={3}>
                    <Form.Label>Main Section *</Form.Label>
                    <Form.Control
                      type="number"
                      value={p.section}
                      onChange={e =>
                        setRow(index, cur => ({
                          ...cur,
                          section: toNum(e.target.value)
                        }))
                      }
                    />
                  </Col>

                  <Col md={3}>
                    <Form.Label>Color *</Form.Label>
                    <Form.Control
                      value={p.color}
                      onChange={e =>
                        setRow(index, cur => ({
                          ...cur,
                          color: e.target.value
                        }))
                      }
                    />
                  </Col>
                </Row>

                <Row className="mb-2">
                  <Col md={4}>
                    <Form.Label>Total launch (m) *</Form.Label>
                    <Form.Control
                      type="number"
                      value={toNum(p.lengthTotal)}
                      onChange={e =>
                        setRow(index, cur => ({
                          ...cur,
                          lengthTotal: Math.max(0, toNum(e.target.value))
                        }))
                      }
                    />
                    <div className="text-muted fs-10 mt-1">
                      Total = Orders + Stock
                    </div>
                  </Col>

                  <Col md={4}>
                    <Form.Label>To Orders (m)</Form.Label>
                    <Form.Control
                      type="number"
                      value={toNum(p.lengthToOrders)}
                      onChange={e => {
                        const v = toNum(e.target.value);
                        setRow(index, cur => {
                          const maxHere =
                            remainingPoolForKey(cur.productKey, index) +
                            toNum(cur.lengthToOrders);
                          return {
                            ...cur,
                            lengthToOrders: clamp(v, 0, Math.max(0, maxHere))
                          };
                        });
                      }}
                    />
                    <div className="text-muted fs-10 mt-1">
                      Pool total: <b>{fmt(poolTotal)}</b> • Available now:{' '}
                      <b>{fmt(poolLeftExclThis)}</b> • After:{' '}
                      <b>{fmt(after)}</b>
                    </div>
                  </Col>

                  <Col md={4}>
                    <Form.Label>To Stock (m)</Form.Label>
                    <Form.Control
                      type="number"
                      value={toNum(p.lengthToStock)}
                      onChange={e => {
                        const stock = Math.max(0, toNum(e.target.value));
                        setRow(index, cur => ({
                          ...cur,
                          lengthTotal: toNum(cur.lengthToOrders) + stock
                        }));
                      }}
                    />
                    <div className="text-muted fs-10 mt-1">
                      Auto = Total − Orders
                    </div>
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col md={8} className="d-flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() =>
                        setRow(index, cur => {
                          const maxHere =
                            remainingPoolForKey(cur.productKey, index) +
                            toNum(cur.lengthToOrders);
                          const next = Math.min(
                            toNum(cur.lengthTotal),
                            maxHere
                          );
                          return { ...cur, lengthToOrders: Math.max(0, next) };
                        })
                      }
                    >
                      Set Orders = min(Total, Available)
                    </Button>

                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() =>
                        setRow(index, cur => {
                          const maxHere =
                            remainingPoolForKey(cur.productKey, index) +
                            toNum(cur.lengthToOrders);
                          return {
                            ...cur,
                            lengthToOrders: Math.max(0, maxHere)
                          };
                        })
                      }
                    >
                      Cover remaining orders
                    </Button>
                  </Col>

                  <Col md={4}>
                    <Form.Label>Spec</Form.Label>
                    <div className="text-muted">{specOf(p)}</div>
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col md={4}>
                    <div className="text-muted fs-10 mb-1">
                      Orders coverage (this productKey)
                    </div>
                    <div style={{ width: 260 }}>
                      <ProgressBar now={coveragePct} style={{ height: 6 }} />
                    </div>
                    <div className="text-muted fs-10 mt-1">{coveragePct}%</div>
                  </Col>
                </Row>

                  </Col>
                  <Col lg={5}>
                {/* Order Reservation — always open (no toggle). Reserve the launched
                    meters onto the order final drums/coils; carried into the wizard. */}
                {(() => {
                  const ctx = buildOrdersContext(p.rawItems);
                  if (!ctx.length) return null;
                  const ACCENT = "#6366f1";
                  const reservation = p._reservation || {};
                  const otherReservedOf = itemId =>
                    (products || []).reduce(
                      (s, row, ri) => (ri === index ? s : s + (Number(row?._reservation?.[itemId]) || 0)),
                      0
                    );
                  const dotHex = resColorHexOf(p.color);
                  const toOrders = toNum(p.lengthToOrders);
                  const reservedM = ctx.reduce(
                    (s, ord) =>
                      s +
                      (ord.deliveryPlanItems || []).reduce(
                        (ss, it) => ss + (Number(reservation[it.itemId]) || 0) * (Number(it.lengthPerUnit) || 0),
                        0
                      ),
                    0
                  );
                  const pct = toOrders > 0 ? Math.min(100, Math.round((reservedM / toOrders) * 100)) : 0;
                  const done = toOrders > 0 && reservedM === toOrders;
                  const over = reservedM > toOrders;
                  return (
                    <div className="mt-3 rounded overflow-hidden" style={{ border: `1.5px solid ${ACCENT}` }}>
                      <div className="d-flex justify-content-between align-items-center px-3 py-2 flex-wrap gap-2" style={{ background: `${ACCENT}1f` }}>
                        <span className="fw-semibold d-flex align-items-center gap-2">
                          <ProductColorDot colorName={p.color} />
                          <span style={{ color: ACCENT }}>Order Reservation — final drums</span>
                        </span>
                        <span className="fw-bold fs-9 px-2 py-1 rounded" style={{ background: done ? "#22c55e" : over ? "#ef4444" : ACCENT, color: "#fff" }}>
                          {fmt(reservedM)} / {fmt(toOrders)} m {done ? "✓" : over ? "· over" : "reserved"}
                        </span>
                      </div>
                      <div className="p-3" style={{ maxHeight: 320, overflowY: "auto" }}>
                        <div className="rounded-pill mb-3" style={{ height: 8, background: "rgba(99,102,241,0.15)", overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: done ? "#22c55e" : over ? "#ef4444" : ACCENT, transition: "width .2s" }} />
                        </div>
                        <div className="d-flex flex-wrap gap-3 align-items-start">
                        {ctx.map((ord, oi) => (
                          <div key={oi} className="mb-1" style={{ flex: "1 1 240px", minWidth: 240 }}>
                            <Badge pill style={{ background: ACCENT, color: "#fff" }}>{ord.orderCode || "—"}</Badge>{" "}
                            <span className="text-body-tertiary fs-10">{ord.client || ""}</span>
                            {(ord.deliveryPlanItems || []).length === 0 ? (
                              <div className="text-body-tertiary fs-10 mt-1">No delivery plan drums to reserve.</div>
                            ) : (
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {(ord.deliveryPlanItems || []).map((it, ii) => {
                                  const kind = String(it.kind || "").toUpperCase();
                                  const len = Number(it.lengthPerUnit) || 0;
                                  const planned = Number(it.qtyPlanned) || 0;
                                  const avail = Math.max(0, planned - otherReservedOf(it.itemId));
                                  const qty = Number(reservation[it.itemId]) || 0;
                                  const specs = kind === "DRUM" ? ([it.drumMaterial, it.drumDiameter ? `Ø${it.drumDiameter}` : ""].filter(Boolean).join(" ") || "Drum") : "Coil";
                                  const canInc = qty < avail && reservedM + len <= toOrders;
                                  // "Max" = reserve as many as possible in one click,
                                  // capped by the order need (avail) AND the launched
                                  // meters this job can produce (toOrders).
                                  const budgetRoom = toOrders > 0 ? Math.max(0, toOrders - (reservedM - qty * len)) : Infinity;
                                  const maxByBudget = len > 0 ? Math.floor(budgetRoom / len) : 0;
                                  const maxUnits = Math.max(0, Math.min(avail, maxByBudget));
                                  return (
                                    <div key={ii} className="rounded p-2" style={{ background: `${dotHex}14`, border: `1px solid ${ACCENT}44`, borderLeft: `4px solid ${dotHex}`, minWidth: 176 }}>
                                      <div className="fw-semibold" style={{ fontSize: 11 }}>{kind === "DRUM" ? "🛢 " : "🎗 "}{specs}</div>
                                      <div className="text-body-tertiary" style={{ fontSize: 10 }}>{fmt(len)} m / buc</div>
                                      <div className="d-flex align-items-center gap-2 mt-2">
                                        <Button size="sm" variant="outline-danger" className="py-0 px-2 fw-bold" disabled={qty <= 0} onClick={() => setReservationQty(index, it.itemId, qty - 1, avail)}>−</Button>
                                        <span className="fw-bold text-center" style={{ minWidth: 44, fontSize: 16 }}>{qty}</span>
                                        <Button size="sm" variant="outline-success" className="py-0 px-2 fw-bold" disabled={!canInc} title={qty >= avail ? "Max available reached (rest reserved by other jobs of this order)" : reservedM + len > toOrders ? "Exceeds the launched meters" : ""} onClick={() => setReservationQty(index, it.itemId, qty + 1, avail)}>+</Button>
                                        <span className="text-body-tertiary fs-10">/ {avail}</span>
                                      </div>
                                      {maxUnits > qty && (
                                        <Button
                                          size="sm"
                                          variant="outline-primary"
                                          className="py-0 px-2 mt-2 w-100 fw-bold"
                                          style={{ fontSize: 10 }}
                                          title="Reserve the maximum this job can produce for the order"
                                          onClick={() => setReservationQty(index, it.itemId, maxUnits, avail)}
                                        >
                                          Max ({maxUnits})
                                        </Button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                        </div>
                        <div className="text-body-tertiary fs-10 fst-italic">Reserve how many of each type — max = what the order needs, capped by the launched meters. Kept into the wizard.</div>
                      </div>
                    </div>
                  );
                })()}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          );
        })}

        <Row className="mt-4">
          <Col className="d-flex gap-2">
            <Button variant="outline-primary" onClick={addCard}>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Add Pair Product
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
}
